/**
 * Sync Service — fetches data from the Trac-IT backend API and
 * persists it to IndexedDB (Dexie) for offline access.
 *
 * Photo pre-caching: after every data sync, iterates all item codes
 * and fires a background fetch for each product photo URL.  The Service
 * Worker intercepts these requests with its CacheFirst handler so that
 * photos are available for offline PDF generation.
 */

import apiClient, { API_BASE_URL } from './apiClient';
import { productPhotoUrl } from './photoCache';
import { db } from './db';
import type { ItemRow, Category5Row, CompanyRow, CatalogueSettings } from '../types';

const ITEMS_URL = '/api/itemtbl/';
const CATEGORIES5_URL = '/api/category5-tbl/';
const COMPANIES_URL = '/api/companytbl/';
const SETTINGS_URL = '/api/branding/catalogue-settings';

/** Fetch all pages of a paginated list endpoint (page_size=1000). */
async function fetchAllPages<T>(url: string): Promise<T[]> {
  const results: T[] = [];
  let page = 1;

  while (true) {
    const response = await apiClient.get<{ items?: T[]; results?: T[] } | T[]>(url, {
      params: { page, page_size: 1000 },
    });

    const raw = response.data;
    let page_items: T[];

    if (Array.isArray(raw)) {
      page_items = raw;
    } else if (Array.isArray((raw as { items?: T[] }).items)) {
      page_items = (raw as { items: T[] }).items;
    } else if (Array.isArray((raw as { results?: T[] }).results)) {
      page_items = (raw as { results: T[] }).results;
    } else {
      break;
    }

    results.push(...page_items);
    if (page_items.length < 1000) break;
    page++;
  }

  return results;
}

/**
 * Pre-cache all product photos by requesting them from the backend.
 * The Service Worker's StaleWhileRevalidate handler stores each response
 * in the 'product-photos' cache so they're available offline.
 *
 * Uses cache: 'no-cache' so the SW always revalidates against the server,
 * picking up new marketing photos (e.g. .png replacing .jpg).
 *
 * Runs in a low-priority background loop to avoid blocking the UI.
 */
async function preCachePhotos(items: ItemRow[], photoCacheBust: number): Promise<void> {
  const codes = items
    .map(i => i.itemcode)
    .filter((c): c is string => !!c);

  for (let i = 0; i < codes.length; i++) {
    const code = codes[i];
    const url = productPhotoUrl(code, photoCacheBust);
    try {
      await fetch(url, { method: 'GET', cache: 'no-cache' });
    } catch {
      // 404 or network error — photo doesn't exist, skip silently
    }
    if (i % 10 === 9) await new Promise(r => setTimeout(r, 0));
  }
}

/** Pre-cache critical branding assets (banner, logo, fonts). */
async function preCacheBrandingAssets(settings: CatalogueSettings): Promise<void> {
  const urls = [
    `${API_BASE_URL}/api/branding/catalogue-banner`,
    `${API_BASE_URL}/api/branding/logo/${encodeURIComponent(settings.logo_file)}`,
    ...Object.values(settings.font_files).map(
      f => `${API_BASE_URL}/api/branding/font/${encodeURIComponent(f)}`
    ),
  ];

  await Promise.allSettled(
    urls.map(url => fetch(url, { method: 'GET', cache: 'force-cache' }))
  );
}

export type SyncResult = {
  itemCount: number;
  syncedAt: Date;
};

/**
 * Full sync: fetch items + categories + companies + settings from the API,
 * bulk-write to IndexedDB, then kick off photo pre-caching in the background.
 */
export async function runSync(
  onProgress?: (msg: string) => void,
  photoCacheBust: number = Date.now(),
): Promise<SyncResult> {
  onProgress?.('Fetching products…');
  console.info('[Sync] Starting full sync — fetching items, categories, companies, settings');

  let items: ItemRow[] = [], categories5: Category5Row[] = [], companies: CompanyRow[] = [], settingsRes: CatalogueSettings | null = null;
  try {
    [items, categories5, companies, settingsRes] = await Promise.all([
      fetchAllPages<ItemRow>(ITEMS_URL),
      fetchAllPages<Category5Row>(CATEGORIES5_URL),
      fetchAllPages<CompanyRow>(COMPANIES_URL),
      apiClient.get<CatalogueSettings>(SETTINGS_URL).then(r => r.data).catch((e) => {
        console.warn('[Sync] Could not fetch catalogue settings:', e?.response?.status, e?.message);
        return null;
      }),
    ]);
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    const msg = (err as Error)?.message ?? String(err);
    console.error('[Sync] API fetch failed — status:', status, msg);
    throw err;
  }

  console.info(`[Sync] Fetched ${items.length} items, ${categories5.length} categories, ${companies.length} companies`);

  onProgress?.(`Writing ${items.length} products to local storage…`);

  await db.transaction('rw', [db.items, db.categories5, db.companies, db.settings, db.syncMeta], async () => {
    await db.items.clear();
    await db.items.bulkAdd(items);

    await db.categories5.clear();
    await db.categories5.bulkAdd(categories5);

    await db.companies.clear();
    await db.companies.bulkAdd(companies);

    if (settingsRes) {
      await db.settings.put({ ...settingsRes, id: 'main' });
    }

    await db.syncMeta.put({ id: 'main', lastSyncedAt: Date.now() });
  });

  const syncedAt = new Date();

  // Pre-cache branding assets and product photos in the background
  if (settingsRes) {
    preCacheBrandingAssets(settingsRes).catch(() => {});
  }
  onProgress?.(`Pre-caching ${items.length} product photos (offline PDF)…`);
  preCachePhotos(items, photoCacheBust).catch(() => {});

  return { itemCount: items.length, syncedAt };
}
